import lscache from 'lscache';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react';
import { useLocation } from 'react-router-dom';
import { isInUsTimezone } from 'utils/timezones/usTimezones';

import { useCurrenciesListContext } from '../CurrencyListContext';
import actionsFactory from './actionsFactory';

export type CurrencyState = {
  currency: string;
};

const initialState: CurrencyState = {
  currency: lscache.get('CURRENCY') ?? (isInUsTimezone() ? 'USD' : 'EUR'),
};

function reducer(state, action) {
  return { ...state, ...action.payload };
}

const initialContext = {
  state: initialState,
  actions: actionsFactory(() => {}),
};

const CurrencyContext = createContext(initialContext);
const useCurrencyContext = () => useContext(CurrencyContext);

function CurrencyContextProvider({ children }) {
  const location = useLocation();
  const [state, dispatcher] = useReducer(reducer, initialState);

  const { state: currenciesListState } = useCurrenciesListContext();
  const { currencies } = currenciesListState;

  const actions = useMemo(() => actionsFactory(dispatcher), [dispatcher]);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('currency')) {
      const currencyCode = searchParams.get('currency');
      const currency = currencies.find(
        ({ code }) => currencyCode.toLowerCase() === code.toLowerCase(),
      );
      if (currency) {
        actions.setCurrency(currency.code);
      }
    }
  }, [location.search, actions, currencies]);

  const contextValue = { state, actions };

  return (
    <CurrencyContext.Provider value={contextValue}>
      {children}
    </CurrencyContext.Provider>
  );
}

export { useCurrencyContext, CurrencyContextProvider };
